import React, { useState } from "react";
import { Link } from "react-router-dom";
import { siWhatsapp } from "simple-icons";
import { ChevronDown, Copy, MessageCircle, Plus, Search, Trash2, Wrench, X } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import LogoUpload from "../LogoUpload";
import BubbleIconPicker from "../BubbleIconPicker";
import { BOTTONE, GRIGIO, SCURO } from "../integrations/casella";
import { AREA_TESTO, CAMPO_TESTO, Campo, Colore, Cursore, Gruppo, Passi, RigaInterruttore, Scelte } from "./campi";

/**
 * Le schede della pagina Widget, in ordine di ragionamento: che cosa
 * mettere sul sito, cosa dice, come appare, dove sta, quando compare, e
 * (solo per la barra) i suoi strumenti. Ogni scheda riceve la
 * configurazione e `cambia(percorso, valore)`.
 */

export const FONT = [
  { value: "Inter", label: "Inter", type: "google" },
  { value: "Roboto", label: "Roboto", type: "google" },
  { value: "Roboto Mono", label: "Roboto Mono", type: "google" },
  { value: "Open Sans", label: "Open Sans", type: "google" },
  { value: "Lato", label: "Lato", type: "google" },
  { value: "Montserrat", label: "Montserrat", type: "google" },
  { value: "Poppins", label: "Poppins", type: "google" },
  { value: "Oswald", label: "Oswald", type: "google" },
  { value: "Source Sans 3", label: "Source Sans 3", type: "google" },
  { value: "Slabo 27px", label: "Slabo", type: "google" },
  { value: "Outfit", label: "Outfit", type: "google" },
  { value: "Raleway", label: "Raleway", type: "google" },
  { value: "Times New Roman", label: "Times New Roman", type: "system" },
  { value: "Arial", label: "Arial", type: "system" },
  { value: "Georgia", label: "Georgia", type: "system" },
  { value: "Verdana", label: "Verdana", type: "system" },
  { value: "Courier New", label: "Courier New", type: "system" },
];

// Gli stili di partenza. Le animazioni sono solo quelle che il widget
// conosce (prima "bounce" e "pulse" lasciavano la tendina vuota).
export const PRESET = {
  default: { primaryColor: "#2563eb", secondaryColor: "#64748b", backgroundColor: "#ffffff", textColor: "#1f2937", gradientEnabled: false, borderRadius: 8, shadowIntensity: 4, animationStyle: "fade", darkMode: false },
  minimal: { primaryColor: "#000000", secondaryColor: "#6b7280", backgroundColor: "#ffffff", textColor: "#000000", gradientEnabled: false, borderRadius: 4, shadowIntensity: 1, animationStyle: "none", darkMode: false },
  modern: { primaryColor: "#8b5cf6", secondaryColor: "#a855f7", backgroundColor: "#ffffff", textColor: "#1f2937", gradientEnabled: true, gradientColors: ["#8b5cf6", "#06b6d4"], borderRadius: 16, shadowIntensity: 8, animationStyle: "slide", darkMode: false },
  colorful: { primaryColor: "#f59e0b", secondaryColor: "#ef4444", backgroundColor: "#fef3c7", textColor: "#92400e", gradientEnabled: true, gradientColors: ["#f59e0b", "#ef4444", "#8b5cf6"], borderRadius: 20, shadowIntensity: 6, animationStyle: "fade", darkMode: false },
  dark: { primaryColor: "#4f46e5", secondaryColor: "#6366f1", backgroundColor: "#1f2937", textColor: "#ffffff", gradientEnabled: false, borderRadius: 8, shadowIntensity: 10, animationStyle: "fade", darkMode: true },
};

/** Il preset che corrisponde al tema attuale, se c'e': per evidenziarlo. */
export function presetAttuale(theme = {}) {
  const uguale = (a, b) => String(a || "").toLowerCase() === String(b || "").toLowerCase();
  return (
    Object.keys(PRESET).find((nome) => {
      const p = PRESET[nome];
      return (
        uguale(p.primaryColor, theme.primaryColor) &&
        uguale(p.backgroundColor, theme.backgroundColor) &&
        uguale(p.textColor, theme.textColor) &&
        Boolean(p.darkMode) === Boolean(theme.darkMode)
      );
    }) || null
  );
}

const Selezione = ({ id, valore, onCambia, children, etichetta }) => (
  <Select value={valore} onValueChange={onCambia}>
    <SelectTrigger
      id={id}
      aria-label={etichetta}
      className="h-10 rounded-lg border-black/10 bg-white text-sm text-[#020617] focus:ring-0 focus:ring-offset-0 focus-visible:ring-2 focus-visible:ring-ring"
    >
      <SelectValue />
    </SelectTrigger>
    <SelectContent className="rounded-xl">{children}</SelectContent>
  </Select>
);

/**
 * Una misura scelta a colpo d'occhio (es. Stretta / Normale / Larga), con
 * «Su misura» per chi vuole il numero esatto: un solo comando, non tre.
 * `null` vuol dire il valore predefinito del widget.
 */
function Misura({ id, etichetta, valore, predefinito, voci, min, max, step, unita, onCambia, t }) {
  const preimpostati = voci.map((v) => v.valore);
  const [suMisura, setSuMisura] = useState(valore !== null && valore !== undefined && !preimpostati.includes(valore));
  const attuale = valore ?? predefinito;
  return (
    <div className="space-y-3">
      <Scelte
        id={id}
        etichetta={etichetta}
        colonne={voci.length + 1}
        voci={[
          ...voci.map((v) => ({
            ...v,
            attiva: !suMisura && (v.valore === null ? valore === null || valore === undefined || valore === predefinito : valore === v.valore),
          })),
          { valore: "__misura", etichetta: t("widget.fields.custom", "Su misura"), attiva: suMisura },
        ]}
        onCambia={(v) => {
          if (v === "__misura") {
            setSuMisura(true);
            return;
          }
          setSuMisura(false);
          onCambia(v);
        }}
      />
      {suMisura ? (
        <Cursore
          etichetta={etichetta}
          valore={attuale}
          formato={(v) => `${String(v).replace(".", ",")}${unita}`}
          min={min}
          max={max}
          step={step}
          onCambia={onCambia}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// 1. Tipo
// ---------------------------------------------------------------------------

/**
 * I testi che il widget ha di suo, uguali a quelli del server
 * (routes/widget.js): li usa anche predefiniti() nella pagina Widget.
 */
export const TESTI_PREDEFINITI = {
  subtitle: "Chiedimi quello che vuoi",
  placeholder: "Scrivi qui il tuo messaggio...",
};

/**
 * Un campo di testo con un valore predefinito: finche' vale quello, il campo
 * resta vuoto e il predefinito si vede in grigio come segnaposto (prima
 * sembrava un testo scritto). Svuotato, torna al predefinito: il widget non
 * resta mai senza testo.
 */
function conPredefinito(valore, predefinito, onCambia) {
  return {
    value: valore && valore !== predefinito ? valore : "",
    placeholder: predefinito,
    onChange: (e) => onCambia(e.target.value || predefinito),
  };
}

// Un sito in miniatura, appena accennato, come quello dietro all'anteprima.
function SitoMini({ children }) {
  return (
    <div className="relative h-[124px] overflow-hidden rounded-[10px] border border-[#E2E8F0] bg-[#F8FAFC]" aria-hidden="true">
      <div className="space-y-2 p-3">
        <div className="h-1.5 w-10 rounded-full bg-[#E2E8F0]" />
        <div className="h-8 rounded-[6px] bg-[#EEF2F6]" />
        <div className="h-1.5 w-3/4 rounded-full bg-[#EEF2F6]" />
        <div className="h-1.5 w-1/2 rounded-full bg-[#EEF2F6]" />
      </div>
      {children}
    </div>
  );
}

// La chat: la finestra aperta sopra la bolla, in basso a destra.
function MiniChat({ colore }) {
  return (
    <SitoMini>
      <div className="absolute bottom-10 right-3 w-[84px] overflow-hidden rounded-[8px] bg-white shadow-[0_6px_16px_rgba(15,23,42,0.14)] ring-1 ring-black/5 transition-transform duration-300 group-hover:-translate-y-0.5">
        <div className="h-3.5" style={{ background: colore }} />
        <div className="space-y-1.5 p-1.5">
          <div className="h-2.5 w-10 rounded-[4px] bg-[#EEF2F6]" />
          <div className="ml-auto h-2.5 w-8 rounded-[4px]" style={{ background: colore, opacity: 0.85 }} />
          <div className="h-2.5 w-12 rounded-[4px] bg-[#EEF2F6]" />
        </div>
      </div>
      <div
        className="absolute bottom-2.5 right-3 flex h-6 w-6 items-center justify-center rounded-full shadow-[0_4px_10px_rgba(15,23,42,0.18)]"
        style={{ background: colore }}
      >
        <MessageCircle className="h-3 w-3 text-white" strokeWidth={2.5} />
      </div>
    </SitoMini>
  );
}

// La barra: in basso al centro, con la ricerca e il pulsante per chiedere.
function MiniBarra({ colore }) {
  return (
    <SitoMini>
      <div className="absolute bottom-3 left-1/2 flex h-7 w-[78%] -translate-x-1/2 items-center gap-1.5 rounded-full bg-white pl-2.5 pr-1 shadow-[0_6px_16px_rgba(15,23,42,0.14)] ring-1 ring-black/5 transition-transform duration-300 group-hover:-translate-y-0.5">
        <Search className="h-3 w-3 shrink-0 text-[#94A3B8]" strokeWidth={2.5} />
        <div className="h-1.5 flex-1 rounded-full bg-[#E2E8F0]" />
        <div className="h-5 w-5 shrink-0 rounded-full" style={{ background: colore }} />
      </div>
    </SitoMini>
  );
}

/**
 * La scelta fra chat e barra: ogni scheda mostra in piccolo come appare sul
 * sito, con il colore del widget. Come Scelte, e' un gruppo di radio.
 */
function SceltaTipo({ valore, onCambia, voci, colore }) {
  const tinta = /^#[0-9a-f]{3,8}$/i.test(colore || "") ? colore : "#2563eb";
  return (
    <div role="radiogroup" className="grid gap-3 sm:grid-cols-2">
      {voci.map(({ valore: v, etichetta, descrizione, Icona, Miniatura }) => {
        const scelta = v === valore;
        return (
          <button
            key={v}
            type="button"
            role="radio"
            aria-checked={scelta}
            onClick={() => onCambia(v)}
            className={`group rounded-[12px] border bg-white p-2 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
              scelta ? "border-[#18181B] shadow-[0_0_0_1px_#18181B]" : "border-[#E2E8F0] hover:border-[#CBD5E1]"
            }`}
          >
            <Miniatura colore={tinta} />
            <span className="flex items-center gap-2 px-1.5 pb-1 pt-3">
              <Icona className="h-4 w-4 text-[#475569]" aria-hidden="true" />
              <span className="text-sm font-medium text-[#020617]">{etichetta}</span>
              <span
                className={`ml-auto flex h-4 w-4 items-center justify-center rounded-full border ${
                  scelta ? "border-[#18181B] bg-[#18181B]" : "border-[#CBD5E1] bg-white"
                }`}
                aria-hidden="true"
              >
                {scelta ? <span className="h-1.5 w-1.5 rounded-full bg-white" /> : null}
              </span>
            </span>
            <span className="block px-1.5 pb-1 text-xs text-[#64748B]">{descrizione}</span>
          </button>
        );
      })}
    </div>
  );
}

export function SezioneTipo({ config, cambia, t, barraAbilitata, codice, onCopiaCodice, botId }) {
  return (
    <Passi>
      {barraAbilitata ? (
        <Gruppo
          titolo={t("widget.sections.typeTitle", "Come appare sul sito")}
          descrizione={t("widget.basic.modeDesc", "Scegli come appare l'assistente sul sito.")}
        >
          <SceltaTipo
            valore={config.mode || "widget"}
            onCambia={(v) => cambia("mode", v)}
            colore={config.theme?.primaryColor}
            voci={[
              {
                valore: "widget",
                etichetta: t("widget.basic.modeWidget", "Chat widget"),
                descrizione: t("widget.basic.modeWidgetDesc", "Bolla flottante con chat"),
                Icona: MessageCircle,
                Miniatura: MiniChat,
              },
              {
                valore: "searchbar",
                etichetta: t("widget.basic.modeSearchbar", "Barra di ricerca"),
                descrizione: t("widget.basic.modeSearchbarDesc", "Barra AI in basso"),
                Icona: Search,
                Miniatura: MiniBarra,
              },
            ]}
          />
          <p className="text-xs leading-5 text-[#64748B]">
            {t(
              "widget.sections.typeHint",
              "Le altre schede cambiano con questa scelta: vedi solo le impostazioni che valgono per il tipo scelto."
            )}
          </p>
        </Gruppo>
      ) : null}

      <Gruppo
        titolo={t("widget.basic.widgetCode", "Codice widget")}
        descrizione={t("widget.basic.widgetCodeDesc", "Identificativo unico del tuo widget (generato automaticamente e di sola lettura)")}
      >
        <div className="flex items-center gap-2">
          <code className="flex h-10 min-w-0 flex-1 items-center truncate rounded-lg border border-black/10 bg-[#F8FAFC] px-3 font-mono text-sm text-[#020617]">
            {codice || t("common.loading", "Caricamento...")}
          </code>
          <button type="button" onClick={onCopiaCodice} disabled={!codice} className={`${GRIGIO} h-10`}>
            <Copy className="h-3.5 w-3.5" aria-hidden="true" />
            {t("botDetail.widgetIntegration.copy", "Copia")}
          </button>
        </div>
        <p className="text-xs text-[#64748B]">
          {t("widget.sections.installHint", "Per metterlo sul sito, con WordPress o con il codice da incollare:")}{" "}
          <Link to={`/bots/${botId}`} className="font-medium text-[#18181B] underline underline-offset-2">
            {t("nav.overview", "Panoramica")}
          </Link>
        </p>
      </Gruppo>
    </Passi>
  );
}

// ---------------------------------------------------------------------------
// 2. Testi
// ---------------------------------------------------------------------------

export function SezioneTesti({ config, cambia, t, barra, nomeBot }) {
  if (barra) {
    return (
      <Gruppo
        titolo={t("widget.sections.searchbarText", "Testo della barra")}
        descrizione={t("widget.sections.searchbarTextDesc", "Quello che il visitatore legge nella barra prima di scrivere.")}
      >
        <Campo id="sb-placeholder" etichetta={t("widget.placeholder", "Testo segnaposto")}>
          <input
            id="sb-placeholder"
            className={CAMPO_TESTO}
            value={config.searchbar?.placeholder ?? ""}
            placeholder="Cerca o chiedi qualcosa..."
            onChange={(e) => cambia("searchbar.placeholder", e.target.value)}
          />
        </Campo>
        <p className="text-xs leading-5 text-[#64748B]">
          {t("widget.sections.searchbarTextNote", "Titolo, messaggio di benvenuto e risposte rapide valgono solo per la chat.")}
        </p>
      </Gruppo>
    );
  }

  const risposte = config.behavior?.quickReplies || [];
  const cambiaRisposte = (nuove) => cambia("behavior.quickReplies", nuove);

  return (
    <Passi>
      <Gruppo
        titolo={t("widget.sections.headerTitle", "Intestazione della chat")}
        descrizione={t("widget.sections.headerDesc", "In cima alla chat, sopra i messaggi.")}
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Campo id="titolo" etichetta={t("widget.botTitle", "Titolo")}>
            <input id="titolo" className={CAMPO_TESTO} value={config.title ?? ""} placeholder={nomeBot} onChange={(e) => cambia("title", e.target.value)} />
          </Campo>
          <Campo id="sottotitolo" etichetta={t("widget.subtitle", "Sottotitolo")}>
            <input
              id="sottotitolo"
              className={CAMPO_TESTO}
              {...conPredefinito(config.subtitle, TESTI_PREDEFINITI.subtitle, (v) => cambia("subtitle", v))}
            />
          </Campo>
        </div>
      </Gruppo>

      <Gruppo
        titolo={t("widget.welcomeMessage", "Messaggio di benvenuto")}
        descrizione={t("widget.welcomeMessageDesc", "Il primo messaggio che il visitatore vede aprendo la chat.")}
      >
        <textarea
          aria-label={t("widget.welcomeMessage", "Messaggio di benvenuto")}
          rows={3}
          className={AREA_TESTO}
          value={config.welcomeMessage ?? ""}
          placeholder={t("widget.basic.welcomePlaceholder", "Ciao! Come posso aiutarti?")}
          onChange={(e) => cambia("welcomeMessage", e.target.value)}
        />
      </Gruppo>

      <Gruppo
        titolo={t("widget.iceBreaker.quickReplyButtons", "Risposte rapide")}
        descrizione={t("widget.iceBreaker.quickReplyButtonsDesc", "Fino a 3 pulsanti con le domande più comuni, sotto il benvenuto.")}
      >
        <div className="space-y-2">
          {risposte.map((risposta, i) => (
            <div key={i} className="flex gap-2">
              <input
                aria-label={`${t("widget.iceBreaker.quickReplyPlaceholder", "Risposta rapida")} ${i + 1}`}
                className={CAMPO_TESTO}
                value={risposta}
                placeholder={t("widget.iceBreaker.quickReplyPlaceholder", "Testo della risposta rapida")}
                onChange={(e) => cambiaRisposte(risposte.map((r, j) => (j === i ? e.target.value : r)))}
              />
              <button
                type="button"
                aria-label={t("common.remove", "Rimuovi")}
                onClick={() => cambiaRisposte(risposte.filter((_, j) => j !== i))}
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-[#94A3B8] hover:bg-[#F1F5F9] hover:text-[#334155]"
              >
                <X className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
          ))}
          <button type="button" className={`${GRIGIO} w-full`} disabled={risposte.length >= 3} onClick={() => cambiaRisposte([...risposte, ""])}>
            {risposte.length >= 3
              ? t("widget.iceBreaker.maxQuickReplies", "Massimo 3 risposte rapide")
              : t("widget.iceBreaker.addQuickReply", "+ Aggiungi risposta rapida")}
          </button>
        </div>
      </Gruppo>

      <Gruppo titolo={t("widget.placeholder", "Testo segnaposto")} descrizione={t("widget.placeholderDesc", "Il testo grigio nel campo dove il visitatore scrive.")}>
        <input
          aria-label={t("widget.placeholder", "Testo segnaposto")}
          className={CAMPO_TESTO}
          {...conPredefinito(config.placeholder, TESTI_PREDEFINITI.placeholder, (v) => cambia("placeholder", v))}
        />
        <p className="text-xs leading-5 text-[#64748B]">
          {t("widget.sections.defaultHint", "Se lo lasci vuoto, resta quello predefinito (in grigio).")}
        </p>
      </Gruppo>
    </Passi>
  );
}

// ---------------------------------------------------------------------------
// 3. Aspetto
// ---------------------------------------------------------------------------

export function SezioneAspetto({ config, cambia, t, barra, botId, onPreset }) {
  const tema = config.theme || {};
  const fontSelect = (
    <Campo id="font" etichetta={t("widget.theme.fontFamily", "Carattere")}>
      <Selezione id="font" etichetta={t("widget.theme.fontFamily", "Carattere")} valore={tema.fontFamily} onCambia={(v) => cambia("theme.fontFamily", v)}>
        {FONT.map((f) => (
          <SelectItem key={f.value} value={f.value}>
            <span style={{ fontFamily: f.value }}>{f.label}</span>
          </SelectItem>
        ))}
      </Selezione>
    </Campo>
  );

  if (barra) {
    return (
      <Gruppo titolo={t("widget.sections.searchbarLook", "Aspetto della barra")}>
        <Scelte
          etichetta={t("widget.basic.theme", "Tema")}
          id="sb-tema"
          valore={config.searchbar?.theme || "light"}
          onCambia={(v) => cambia("searchbar.theme", v)}
          voci={[
            { valore: "light", etichetta: t("widget.basic.themeLight", "Chiaro"), descrizione: t("widget.basic.themeLightDesc", "Vetro chiaro, testo scuro") },
            { valore: "dark", etichetta: t("widget.basic.themeDark", "Scuro"), descrizione: t("widget.basic.themeDarkDesc", "Vetro scuro, testo chiaro") },
          ]}
        />
        {fontSelect}
        <Misura
          id="sb-larghezza"
          t={t}
          etichetta={t("widget.basic.sbWidth", "Larghezza della barra")}
          valore={config.searchbar?.maxWidth ?? null}
          predefinito={672}
          voci={[
            { valore: 480, etichetta: t("widget.basic.sbWidthNarrow", "Stretta") },
            { valore: null, etichetta: t("widget.basic.sbWidthNormal", "Normale") },
            { valore: 900, etichetta: t("widget.basic.sbWidthWide", "Larga") },
          ]}
          min={280}
          max={1600}
          step={10}
          unita=" px"
          onCambia={(v) => cambia("searchbar.maxWidth", v)}
        />
        <Misura
          id="sb-altezza"
          t={t}
          etichetta={t("widget.basic.sbHeight", "Altezza del pannello risposte")}
          valore={config.searchbar?.panelMaxHeight ?? null}
          predefinito={60}
          voci={[
            { valore: 40, etichetta: t("widget.basic.sbHeightCompact", "Compatta") },
            { valore: null, etichetta: t("widget.basic.sbHeightMedium", "Media") },
            { valore: 80, etichetta: t("widget.basic.sbHeightTall", "Alta") },
          ]}
          min={10}
          max={90}
          step={5}
          unita="%"
          onCambia={(v) => cambia("searchbar.panelMaxHeight", v)}
        />
      </Gruppo>
    );
  }

  const scelto = presetAttuale(tema);
  const coloriSfumatura = Array.isArray(tema.gradientColors) ? tema.gradientColors : ["#2563eb", "#8b5cf6"];

  return (
    <Passi>
      <Gruppo titolo={t("widget.designPresets", "Stile di partenza")} descrizione={t("widget.presetsDesc", "Un punto di partenza: poi puoi cambiare ogni dettaglio.")}>
        <Scelte
          colonne={5}
          valore={scelto}
          onCambia={onPreset}
          voci={Object.keys(PRESET).map((nome) => ({
            valore: nome,
            etichetta: t(`widget.${nome}`, nome),
            segno: (
              <span
                className="h-4 w-4 shrink-0 rounded-full ring-1 ring-black/10"
                style={{
                  background: PRESET[nome].gradientEnabled
                    ? `linear-gradient(135deg, ${PRESET[nome].gradientColors.join(", ")})`
                    : PRESET[nome].primaryColor,
                }}
                aria-hidden="true"
              />
            ),
          }))}
        />
      </Gruppo>

      <Gruppo titolo={t("widget.sections.colors", "Colori")}>
        <div className="grid gap-4 sm:grid-cols-2">
          <Colore id="c-primario" etichetta={t("widget.primaryColor", "Colore principale")} valore={tema.primaryColor} onCambia={(v) => cambia("theme.primaryColor", v)} />
          <Colore id="c-secondario" etichetta={t("widget.appearance.secondaryColor", "Colore secondario")} valore={tema.secondaryColor} onCambia={(v) => cambia("theme.secondaryColor", v)} />
          <Colore id="c-sfondo" etichetta={t("widget.theme.backgroundColor", "Colore di sfondo")} valore={tema.backgroundColor} onCambia={(v) => cambia("theme.backgroundColor", v)} />
          <Colore id="c-testo" etichetta={t("widget.theme.textColor", "Colore del testo")} valore={tema.textColor} onCambia={(v) => cambia("theme.textColor", v)} />
        </div>
        <RigaInterruttore
          id="scuro"
          etichetta={t("widget.theme.darkMode", "Modalità scura")}
          aiuto={t("widget.sections.darkHint", "Con la modalità scura sfondo e testo li decide il tema scuro.")}
          attivo={tema.darkMode}
          onCambia={(v) => cambia("theme.darkMode", v)}
        />
        <RigaInterruttore id="sfumatura" etichetta={t("widget.theme.enableGradient", "Sfumatura")} attivo={tema.gradientEnabled} onCambia={(v) => cambia("theme.gradientEnabled", v)} />
        {tema.gradientEnabled ? (
          <div className="space-y-4 rounded-[10px] bg-[#F8FAFC] p-4">
            <Cursore
              etichetta={t("widget.sections.gradientDirection", "Direzione")}
              valore={tema.gradientDirection ?? 45}
              formato={(v) => `${v}°`}
              min={0}
              max={360}
              step={15}
              onCambia={(v) => cambia("theme.gradientDirection", v)}
            />
            <div className="grid gap-4 sm:grid-cols-2">
              {coloriSfumatura.map((colore, i) => (
                <Colore
                  key={i}
                  id={`c-sfumatura-${i}`}
                  etichetta={`${t("widget.theme.gradientColors", "Colori della sfumatura")} ${i + 1}`}
                  valore={colore}
                  onCambia={(v) => cambia("theme.gradientColors", coloriSfumatura.map((c, j) => (j === i ? v : c)))}
                />
              ))}
            </div>
          </div>
        ) : null}
      </Gruppo>

      <Gruppo titolo={t("widget.theme.typography", "Testo")}>
        <div className="grid gap-4 sm:grid-cols-2">
          {fontSelect}
          <Cursore
            etichetta={t("widget.theme.fontSize", "Dimensione del carattere")}
            valore={tema.fontSize ?? 14}
            formato={(v) => `${v}px`}
            min={10}
            max={20}
            onCambia={(v) => cambia("theme.fontSize", v)}
          />
        </div>
      </Gruppo>

      <Gruppo titolo={t("widget.theme.layoutEffects", "Forme e movimento")}>
        <div className="grid gap-x-6 gap-y-5 sm:grid-cols-2">
          <Cursore etichetta={t("widget.theme.borderRadius", "Arrotondamento")} valore={tema.borderRadius ?? 8} formato={(v) => `${v}px`} min={0} max={50} onCambia={(v) => cambia("theme.borderRadius", v)} />
          <Cursore etichetta={t("widget.theme.shadowIntensity", "Ombra")} valore={tema.shadowIntensity ?? 4} min={0} max={10} onCambia={(v) => cambia("theme.shadowIntensity", v)} />
          <Cursore etichetta={t("widget.theme.spacing", "Spaziatura")} valore={tema.spacing ?? 16} formato={(v) => `${v}px`} min={8} max={32} step={2} onCambia={(v) => cambia("theme.spacing", v)} />
          <Campo id="animazione" etichetta={t("widget.theme.animationStyle", "Animazione")}>
            <Selezione id="animazione" etichetta={t("widget.theme.animationStyle", "Animazione")} valore={tema.animationStyle || "fade"} onCambia={(v) => cambia("theme.animationStyle", v)}>
              <SelectItem value="none">{t("widget.theme.animationNone", "Nessuna")}</SelectItem>
              <SelectItem value="balloon">{t("widget.theme.animationBalloon", "Fumetto")}</SelectItem>
              <SelectItem value="fade">{t("widget.theme.animationFade", "Dissolvenza")}</SelectItem>
              <SelectItem value="slide">{t("widget.theme.animationSlide", "Scorrimento")}</SelectItem>
            </Selezione>
          </Campo>
        </div>
      </Gruppo>

      <Gruppo titolo={t("widget.theme.logoBranding", "Logo e icona")}>
        {/* Pulsante della chat: la bolla con un'icona, oppure la mascotte 3D PingUp (battito, sguardo
            verso la chat aperta, tre pallini mentre il bot scrive la risposta) */}
        <Scelte
          etichetta={t("widget.theme.buttonStyle", "Pulsante della chat")}
          id="stile-pulsante"
          valore={tema.buttonStyle || "icon"}
          onCambia={(v) => cambia("theme.buttonStyle", v)}
          voci={[
            {
              valore: "icon",
              etichetta: t("widget.theme.buttonStyleIcon", "Bolla con icona"),
              descrizione: t("widget.theme.buttonStyleIconDesc", "Il pulsante rotondo del colore principale."),
            },
            {
              valore: "mascotte3d",
              etichetta: t("widget.theme.buttonStyleMascot", "Mascotte 3D PingUp"),
              descrizione: t("widget.theme.buttonStyleMascotDesc", "Il logo PingUp in 3D, animato, al posto della bolla."),
            },
          ]}
        />
        <div className="grid gap-5 sm:grid-cols-2">
          <Campo etichetta={t("widget.theme.logoUrl", "Logo")}>
            <LogoUpload value={tema.logoUrl} botId={botId} onChange={(url) => cambia("theme.logoUrl", url)} />
          </Campo>
          {tema.buttonStyle === "mascotte3d" ? null : (
            <Campo etichetta={t("widget.theme.bubbleIcon", "Icona della bolla")}>
              <BubbleIconPicker value={tema.buttonIcon} onChange={(key) => cambia("theme.buttonIcon", key)} />
            </Campo>
          )}
        </div>
        {tema.logoUrl ? (
          <div className="grid gap-5 sm:grid-cols-2">
            <Cursore etichetta={t("widget.theme.logoHeight", "Altezza del logo")} valore={Math.max(46, tema.logoHeight ?? 48)} formato={(v) => `${v}px`} min={46} max={72} step={2} onCambia={(v) => cambia("theme.logoHeight", v)} />
            <Scelte
              etichetta={t("widget.theme.logoPosition", "Posizione del logo")}
              id="logo-posizione"
              valore={tema.logoPosition || "left"}
              onCambia={(v) => cambia("theme.logoPosition", v)}
              voci={[
                { valore: "left", etichetta: t("widget.theme.logoPositionLeft", "Sinistra") },
                { valore: "right", etichetta: t("widget.theme.logoPositionRight", "Destra") },
              ]}
            />
          </div>
        ) : null}
      </Gruppo>
    </Passi>
  );
}

// ---------------------------------------------------------------------------
// 4. Posizione (solo chat)
// ---------------------------------------------------------------------------

export function SezionePosizione({ config, cambia, t }) {
  const pos = config.position || {};
  return (
    <Gruppo
      titolo={t("widget.position.title", "Posizione e dimensione")}
      descrizione={t("widget.sections.positionDesc", "Dove sta la bolla sul computer. Sul telefono la chat si apre sempre a tutto schermo.")}
    >
      <Scelte
        id="angolo"
        etichetta={t("widget.position.desktopPosition", "Angolo")}
        valore={pos.position || "bottom-right"}
        onCambia={(v) => cambia("position.position", v)}
        voci={[
          { valore: "bottom-right", etichetta: t("widget.position.bottomRight", "In basso a destra") },
          { valore: "bottom-left", etichetta: t("widget.position.bottomLeft", "In basso a sinistra") },
        ]}
      />
      <Scelte
        id="dimensione"
        etichetta={t("widget.position.desktopSize", "Larghezza della chat")}
        colonne={3}
        valore={pos.size || "medium"}
        onCambia={(v) => cambia("position.size", v)}
        voci={[
          { valore: "small", etichetta: t("widget.position.sizeSmall", "Piccola (320px)") },
          { valore: "medium", etichetta: t("widget.position.sizeMedium", "Media (380px)") },
          { valore: "large", etichetta: t("widget.position.sizeLarge", "Grande (440px)") },
        ]}
      />
      <div className="grid gap-x-6 gap-y-5 sm:grid-cols-2">
        <Cursore etichetta={t("widget.position.horizontalOffset", "Distanza dal lato")} valore={pos.offsetX ?? 20} formato={(v) => `${v}px`} min={0} max={100} step={5} onCambia={(v) => cambia("position.offsetX", v)} />
        <Cursore etichetta={t("widget.position.verticalOffset", "Distanza dal fondo")} valore={pos.offsetY ?? 20} formato={(v) => `${v}px`} min={0} max={100} step={5} onCambia={(v) => cambia("position.offsetY", v)} />
      </div>
    </Gruppo>
  );
}

// ---------------------------------------------------------------------------
// 5. Quando compare
// ---------------------------------------------------------------------------

/** Il numero dell'app WhatsApp (solo cifre col prefisso), leggibile. */
export function numeroLeggibile(phone) {
  const cifre = String(phone || "").replace(/\D/g, "");
  if (!cifre) return "";
  const it = cifre.match(/^39(3\d{2})(\d{3})(\d{3,4})$/);
  return it ? `+39 ${it[1]} ${it[2]} ${it[3]}` : `+${cifre}`;
}

const secondi = (ms) => `${Math.round((ms || 0) / 1000)} s`;

export function SezioneComparsa({ config, cambia, t, barra, botId, numeroWhatsapp, integrazioniInCaricamento }) {
  if (barra) {
    return (
      <Gruppo titolo={t("widget.basic.sbDelay", "Quando far apparire la barra")}>
        <Misura
          id="sb-ritardo"
          t={t}
          etichetta={t("widget.basic.sbDelay", "Quando far apparire la barra")}
          valore={config.searchbar?.appearDelay || null}
          predefinito={0}
          voci={[
            { valore: null, etichetta: t("widget.basic.sbDelayNow", "Subito") },
            { valore: 3, etichetta: t("widget.basic.sbDelay3", "Dopo 3 secondi") },
            { valore: 10, etichetta: t("widget.basic.sbDelay10", "Dopo 10 secondi") },
          ]}
          min={0}
          max={60}
          step={0.5}
          unita=" s"
          onCambia={(v) => cambia("searchbar.appearDelay", v)}
        />
      </Gruppo>
    );
  }

  const comp = config.behavior || {};
  const pillola = comp.externalCTA || {};
  const whatsapp = pillola.type === "whatsapp";

  return (
    <Passi>
      <Gruppo
        titolo={t("widget.behavior.autoOpenTitle", "Apertura automatica")}
        descrizione={t("widget.behavior.autoOpenDesc", "La chat si apre da sola, senza che il visitatore clicchi la bolla.")}
      >
        <RigaInterruttore
          id="apertura"
          etichetta={t("widget.behavior.autoOpenWidget", "Apri la chat da sola")}
          aiuto={t("widget.sections.autoOpenHint", "Nell'anteprima non si vede: vale quando il visitatore arriva sulla pagina.")}
          attivo={comp.autoOpen}
          onCambia={(v) => cambia("behavior.autoOpen", v)}
        />
        {comp.autoOpen ? (
          <div className="space-y-4 rounded-[10px] bg-[#F8FAFC] p-4">
            <Scelte
              id="apertura-quando"
              etichetta={t("widget.behavior.autoOpenTrigger", "Quando")}
              colonne={3}
              valore={comp.autoOpenTrigger || "time"}
              onCambia={(v) => cambia("behavior.autoOpenTrigger", v)}
              voci={[
                { valore: "time", etichetta: t("widget.behavior.triggerTime", "Dopo qualche secondo") },
                { valore: "scroll", etichetta: t("widget.behavior.triggerScroll", "Scorrendo la pagina") },
                { valore: "exit-intent", etichetta: t("widget.behavior.triggerExitIntent", "Quando sta per uscire") },
              ]}
            />
            {(comp.autoOpenTrigger || "time") === "time" ? (
              <Cursore etichetta={t("widget.behavior.delay", "Dopo")} valore={comp.autoOpenDelay ?? 3000} formato={secondi} min={1000} max={30000} step={1000} onCambia={(v) => cambia("behavior.autoOpenDelay", v)} />
            ) : null}
            {comp.autoOpenTrigger === "scroll" ? (
              <Cursore etichetta={t("widget.behavior.scrollPercentage", "Dopo aver scorso")} valore={comp.scrollPercentage ?? 50} formato={(v) => `${v}%`} min={10} max={100} step={10} onCambia={(v) => cambia("behavior.scrollPercentage", v)} />
            ) : null}
          </div>
        ) : null}
      </Gruppo>

      <Gruppo
        titolo={t("widget.quickInteraction.title", "Pillola accanto alla bolla")}
        descrizione={t("widget.quickInteraction.description", "Una frase che si apre accanto alla bolla per invitare a scrivere, o a continuare su WhatsApp.")}
      >
        <RigaInterruttore
          id="pillola"
          etichetta={t("widget.quickInteraction.enable", "Mostra la pillola")}
          attivo={pillola.enabled}
          onCambia={(v) => cambia("behavior.externalCTA.enabled", v)}
        />
        {pillola.enabled ? (
          <div className="space-y-5 rounded-[10px] bg-[#F8FAFC] p-4">
            <Scelte
              id="pillola-tipo"
              etichetta={t("widget.sections.pillWhat", "Cosa fa")}
              valore={whatsapp ? "whatsapp" : "message"}
              onCambia={(v) => cambia("behavior.externalCTA.type", v)}
              voci={[
                {
                  valore: "message",
                  etichetta: t("widget.sections.pillMessage", "Apre la chat"),
                  segno: <MessageCircle className="h-4 w-4 text-[#475569]" aria-hidden="true" />,
                },
                {
                  valore: "whatsapp",
                  etichetta: t("widget.sections.pillWhatsapp", "Porta su WhatsApp"),
                  segno: (
                    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="#25D366" aria-hidden="true">
                      <path d={siWhatsapp.path} />
                    </svg>
                  ),
                },
              ]}
            />

            {whatsapp ? (
              <>
                {integrazioniInCaricamento ? null : numeroWhatsapp ? (
                  <p className="text-sm text-[#334155]">
                    {t("widget.quickInteraction.whatsappGoesTo", "Porta a WhatsApp:")}{" "}
                    <strong className="font-semibold text-[#020617]">{numeroLeggibile(numeroWhatsapp)}</strong>{" "}
                    <Link to={`/bots/${botId}/integrazioni/whatsapp`} className="text-xs font-medium text-[#18181B] underline underline-offset-2">
                      {t("widget.quickInteraction.changeNumberInApp", "Cambia numero nell'app WhatsApp")}
                    </Link>
                  </p>
                ) : (
                  <div className="flex flex-wrap items-center justify-between gap-3 rounded-[10px] border border-amber-200 bg-amber-50 p-3">
                    <p className="text-sm text-amber-800">
                      {t("widget.quickInteraction.whatsappAppInactive", "Per usare la pillola WhatsApp attiva prima l'app WhatsApp in Apps & Integrazioni.")}
                    </p>
                    <Link to={`/bots/${botId}/integrazioni/whatsapp`} className={SCURO}>
                      {t("widget.quickInteraction.openWhatsAppApp", "Apri l'app WhatsApp")}
                    </Link>
                  </div>
                )}
                <div className="grid gap-4 sm:grid-cols-2">
                  <Campo id="pillola-testo-wa" etichetta={t("widget.quickInteraction.buttonText", "Testo della pillola")}>
                    <input
                      id="pillola-testo-wa"
                      className={CAMPO_TESTO}
                      value={pillola.whatsappText ?? ""}
                      placeholder={t("widget.quickInteraction.buttonTextPlaceholder", "Scrivici su WhatsApp")}
                      onChange={(e) => cambia("behavior.externalCTA.whatsappText", e.target.value)}
                    />
                  </Campo>
                  <Campo id="pillola-messaggio-wa" etichetta={t("widget.quickInteraction.messageToSend", "Messaggio già scritto")}>
                    <input
                      id="pillola-messaggio-wa"
                      className={CAMPO_TESTO}
                      value={pillola.whatsappMessage ?? ""}
                      placeholder={t("widget.quickInteraction.messagePlaceholder", "Ho bisogno di aiuto")}
                      onChange={(e) => cambia("behavior.externalCTA.whatsappMessage", e.target.value)}
                    />
                  </Campo>
                </div>
              </>
            ) : (
              <Campo
                id="pillola-testo"
                etichetta={t("widget.quickInteraction.customMessage", "Testo della pillola")}
                aiuto={t("widget.quickInteraction.customMessageDesc", "Cliccandola si apre la chat.")}
              >
                {/* Vuoto resta vuoto: il predefinito del widget si vede come segnaposto. */}
                <input
                  id="pillola-testo"
                  className={CAMPO_TESTO}
                  value={pillola.message ?? ""}
                  placeholder="Hai bisogno di aiuto?"
                  onChange={(e) => cambia("behavior.externalCTA.message", e.target.value)}
                />
              </Campo>
            )}

            <Cursore
              etichetta={t("widget.quickInteraction.showAfter", "Compare dopo")}
              valore={pillola.showDelay || 5000}
              formato={secondi}
              min={1000}
              max={30000}
              step={1000}
              onCambia={(v) => cambia("behavior.externalCTA.showDelay", v)}
            />
            <RigaInterruttore
              id="pillola-nascondi"
              etichetta={t("widget.quickInteraction.autoHide", "Si nasconde da sola")}
              attivo={pillola.autoHide !== false}
              onCambia={(v) => cambia("behavior.externalCTA.autoHide", v)}
            />
            {pillola.autoHide !== false ? (
              <Cursore
                etichetta={t("widget.quickInteraction.hideAfter", "Si nasconde dopo")}
                valore={pillola.autoHideDelay || 15000}
                formato={secondi}
                min={5000}
                max={60000}
                step={5000}
                onCambia={(v) => cambia("behavior.externalCTA.autoHideDelay", v)}
              />
            ) : null}
            <p className="text-xs leading-5 text-[#64748B]">
              {t("widget.sections.pillPreviewHint", "Nell'anteprima la pillola compare subito e non si nasconde, per poterla vedere.")}
            </p>
          </div>
        ) : null}
      </Gruppo>
    </Passi>
  );
}

// ---------------------------------------------------------------------------
// 6. Strumenti (solo barra)
// ---------------------------------------------------------------------------

// Modelli pronti: aggiungono uno strumento gia' compilato, da ritoccare.
const MODELLI_STRUMENTI = [
  { key: "summary", label: "Riassumi pagina", icon: "summary", mode: "page", query: "", systemPrompt: "Riassumi il contenuto di questa pagina in modo chiaro e conciso, con un breve elenco puntato dei punti chiave. Mantieni la lingua della pagina e non aggiungere informazioni non presenti." },
  { key: "recommend", label: "Consiglia prodotti", icon: "box", mode: "query", query: "Quali prodotti mi consigli?", systemPrompt: "Sei un consulente vendite cordiale. Consiglia al massimo 3 prodotti adatti alla richiesta, spiegando in una riga il perché di ciascuno. Usa solo prodotti presenti nel catalogo/fonti: non inventarne." },
  { key: "compare", label: "Confronta prodotti", icon: "compare", mode: "query", query: "Confronta i prodotti principali", systemPrompt: "Confronta i prodotti pertinenti evidenziando differenze di prezzo, caratteristiche e casi d'uso, in un elenco o tabella chiara. Basati solo sui dati delle fonti." },
  { key: "support", label: "Assistenza", icon: "question", mode: "query", query: "Ho bisogno di aiuto", systemPrompt: "Sei l'assistente di supporto del sito. Rispondi in modo cortese e conciso basandoti sulle informazioni disponibili. Se non conosci la risposta, indica gentilmente come contattare l'assistenza." },
  { key: "explain", label: "Spiega semplice", icon: "explain", mode: "query", query: "", systemPrompt: "Spiega l'argomento richiesto in modo semplice e accessibile, come a un principiante, con un esempio pratico quando utile. Frasi brevi e chiare." },
  { key: "price", label: "Prezzi e disponibilità", icon: "price", mode: "query", query: "Quanto costa?", systemPrompt: "Fornisci prezzo e disponibilità dei prodotti richiesti in modo diretto e chiaro, usando solo i dati delle fonti. Se un prezzo non è disponibile, dillo esplicitamente." },
];

const ICONE_STRUMENTI = ["summary", "explain", "list", "question", "price", "shipping", "box", "features", "code", "compare", "search", "time", "location", "star"];

const nuovoId = () => (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `tool_${Date.now()}`);

export function SezioneStrumenti({ config, cambia, t }) {
  const strumenti = config.searchbar?.tools || [];
  const [aperti, setAperti] = useState([]);
  const [daTogliere, setDaTogliere] = useState(null);

  const salva = (lista) => cambia("searchbar.tools", lista);
  const aggiungi = (modello) => {
    const id = nuovoId();
    salva([
      ...strumenti,
      modello
        ? { id, label: modello.label, icon: modello.icon, mode: modello.mode, query: modello.query || "", systemPrompt: modello.systemPrompt }
        : { id, label: "", icon: "", mode: "query", query: "", systemPrompt: "" },
    ]);
    setAperti((a) => [...a, id]);
  };
  const aggiorna = (i, campo, valore) => salva(strumenti.map((s, j) => (j === i ? { ...s, [campo]: valore } : s)));
  // Senza strumenti la barra torna ai suggerimenti automatici (una lista
  // vuota invece li spegneva, contro quello che diceva la pagina).
  const togli = (i) => {
    const resto = strumenti.filter((_, j) => j !== i);
    salva(resto.length ? resto : null);
  };

  return (
    <Gruppo
      titolo={t("widget.basic.tools", "Strumenti della barra")}
      descrizione={t("widget.basic.toolsDesc", "Pulsanti pronti nella barra: ognuno fa una domanda o riassume la pagina, con le sue istruzioni.")}
      azioni={
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button type="button" className={SCURO}>
              <Plus className="h-3.5 w-3.5" aria-hidden="true" />
              {t("widget.basic.toolAdd", "Aggiungi strumento")}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56 rounded-xl">
            <DropdownMenuItem onClick={() => aggiungi(null)} className="rounded-lg">
              <Wrench className="mr-2 h-4 w-4 text-[#475569]" />
              {t("widget.basic.toolBlank", "Strumento vuoto")}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-xs font-medium text-[#64748B]">{t("widget.basic.toolTemplates", "Da un modello")}</DropdownMenuLabel>
            {MODELLI_STRUMENTI.map((m) => (
              <DropdownMenuItem key={m.key} onClick={() => aggiungi(m)} className="rounded-lg">
                {m.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      }
    >
      {strumenti.length === 0 ? (
        <div className="rounded-[10px] border border-dashed border-[#CBD5E1] p-6 text-center">
          <Wrench className="mx-auto mb-2 h-5 w-5 text-[#94A3B8]" aria-hidden="true" />
          <p className="text-sm text-[#64748B]">{t("widget.basic.toolsEmpty", "Nessuno strumento: la barra propone suggerimenti automatici.")}</p>
        </div>
      ) : (
        <ul className="space-y-2">
          {strumenti.map((strumento, i) => {
            const aperto = aperti.includes(strumento.id);
            return (
              <li key={strumento.id || i}>
                <Collapsible
                  open={aperto}
                  onOpenChange={(o) => setAperti((a) => (o ? [...new Set([...a, strumento.id])] : a.filter((x) => x !== strumento.id)))}
                  className="rounded-[10px] border border-[#E2E8F0] bg-white"
                >
                  <div className="flex items-center gap-1 p-2 pl-3">
                    <CollapsibleTrigger asChild>
                      <button type="button" className="flex min-w-0 flex-1 items-center gap-2 text-left">
                        <ChevronDown className={`h-4 w-4 shrink-0 text-[#94A3B8] transition-transform ${aperto ? "rotate-180" : ""}`} aria-hidden="true" />
                        <span className="truncate text-sm font-medium text-[#020617]">{strumento.label || t("widget.basic.toolUnnamed", "Nuovo strumento")}</span>
                        <span className="shrink-0 rounded-full bg-[#F1F5F9] px-2 py-0.5 text-[11px] font-medium text-[#475569]">
                          {strumento.mode === "page" ? t("widget.basic.toolModePage", "Riassume la pagina") : t("widget.basic.toolModeQuery", "Fa una domanda")}
                        </span>
                      </button>
                    </CollapsibleTrigger>
                    <button
                      type="button"
                      aria-label={t("common.remove", "Rimuovi")}
                      onClick={() => setDaTogliere({ i, label: strumento.label })}
                      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[8px] text-[#94A3B8] hover:bg-red-50 hover:text-[#B91C1C]"
                    >
                      <Trash2 className="h-4 w-4" aria-hidden="true" />
                    </button>
                  </div>
                  <CollapsibleContent>
                    <div className="space-y-3 border-t border-[#E2E8F0] p-3">
                      <input
                        aria-label={t("widget.basic.toolLabel", "Etichetta")}
                        className={CAMPO_TESTO}
                        placeholder={t("widget.basic.toolLabel", "Etichetta (es. Confronta prodotti)")}
                        value={strumento.label || ""}
                        onChange={(e) => aggiorna(i, "label", e.target.value)}
                      />
                      <div className="grid grid-cols-2 gap-2">
                        <Selezione etichetta={t("widget.sections.toolMode", "Cosa fa")} valore={strumento.mode || "query"} onCambia={(v) => aggiorna(i, "mode", v)}>
                          <SelectItem value="query">{t("widget.basic.toolModeQuery", "Fa una domanda")}</SelectItem>
                          <SelectItem value="page">{t("widget.basic.toolModePage", "Riassume la pagina")}</SelectItem>
                        </Selezione>
                        <Selezione etichetta={t("widget.sections.toolIcon", "Icona")} valore={strumento.icon || "auto"} onCambia={(v) => aggiorna(i, "icon", v === "auto" ? "" : v)}>
                          <SelectItem value="auto">{t("widget.basic.toolIconAuto", "Icona automatica")}</SelectItem>
                          {ICONE_STRUMENTI.map((ic) => (
                            <SelectItem key={ic} value={ic}>
                              {ic}
                            </SelectItem>
                          ))}
                        </Selezione>
                      </div>
                      <input
                        aria-label={t("widget.sections.toolQuery", "Domanda o istruzione")}
                        className={CAMPO_TESTO}
                        placeholder={
                          strumento.mode === "page"
                            ? t("widget.basic.toolInstruction", "Istruzione (facoltativa, es. Riassumi in 5 punti)")
                            : t("widget.basic.toolQuery", "Domanda da inviare (es. Quali prodotti mi consigli?)")
                        }
                        value={strumento.query || ""}
                        onChange={(e) => aggiorna(i, "query", e.target.value)}
                      />
                      <textarea
                        aria-label={t("widget.basic.toolSystemPrompt", "Istruzioni per l'assistente")}
                        rows={3}
                        className={AREA_TESTO}
                        placeholder={t("widget.basic.toolSystemPrompt", "Istruzioni per l'assistente (guidano la risposta)")}
                        value={strumento.systemPrompt || ""}
                        onChange={(e) => aggiorna(i, "systemPrompt", e.target.value)}
                      />
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              </li>
            );
          })}
        </ul>
      )}

      <Dialog open={Boolean(daTogliere)} onOpenChange={(o) => !o && setDaTogliere(null)}>
        <DialogContent className="max-w-md rounded-[14px]">
          <DialogHeader>
            <DialogTitle>{t("widget.basic.toolDeleteTitle", "Eliminare lo strumento?")}</DialogTitle>
            <DialogDescription>
              {t("widget.basic.toolDeleteDesc", "Lo strumento sparisce dalla barra.")}
              {daTogliere?.label ? ` «${daTogliere.label}»` : ""}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <button type="button" className={GRIGIO} onClick={() => setDaTogliere(null)}>
              {t("common.cancel", "Annulla")}
            </button>
            <button
              type="button"
              className={`${BOTTONE} bg-[#DC2626] text-white hover:bg-[#B91C1C]`}
              onClick={() => {
                togli(daTogliere.i);
                setDaTogliere(null);
              }}
            >
              {t("common.delete", "Elimina")}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Gruppo>
  );
}
